//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { CreatorListSort } from '@influencers/constants/creatorListSort';
import { InfluencersTrackingIds } from '@influencers/constants/influencersTrackingIds';
import { IInfluencerBulkAddStatusCode } from '@influencers/models/influencersApiModel';
import { IInfluencersBulkAddStatus } from '@influencers/models/influencersStateModel';
import { SocialNetwork } from '@models/SocialNetwork';
import { TrackingId } from '@models/TrackingId';

export const CampaignSourceType = {
  Existing: 'existing',
  New: 'new',
} as const;

export type CampaignSourceType =
  (typeof CampaignSourceType)[keyof typeof CampaignSourceType];

export enum INFLUENCERS_SEARCH_ACTIONS {
  UPDATE_ADD_SEARCH_TERMS = 'INFLUENCERS/UPDATE_ADD_SEARCH_TERMS',
  REQUEST_SEARCH_RESULTS = 'INFLUENCERS/REQUEST_SEARCH_RESULTS',
  RECEIVE_SEARCH_RESULTS = 'INFLUENCERS/RECEIVE_SEARCH_RESULTS',
  UPDATE_REMOVE_SEARCH_TERM_BY_INDEX = 'INFLUENCERS/UPDATE_REMOVE_SEARCH_TERM_BY_INDEX',
  CLEAR_SEARCH = 'INFLUENCERS/CLEAR_SEARCH',
  CLEAR_SUGGESTED_INFLUENCERS = 'INFLUENCERS/CLEAR_SUGGESTED_INFLUENCERS',
  UPDATE_SEARCH_INPUT = 'INFLUENCERS/UPDATE_SEARCH_INPUT',
  SEARCH_INFLUENCERS_REQUESTED = 'INFLUENCERS/SEARCH_INFLUENCERS_REQUESTED',
  SEARCH_INFLUENCERS_SUCCEEDED = 'INFLUENCERS/SEARCH_INFLUENCERS_SUCCEEDED',
  SEARCH_INFLUENCERS_FAILED = 'INFLUENCERS/SEARCH_INFLUENCERS_FAILED',
  SUGGESTED_INFLUENCERS_REQUESTED = 'INFLUENCERS/SUGGESTED_INFLUENCERS_REQUESTED',
  SUGGESTED_INFLUENCERS_SUCCEEDED = 'INFLUENCERS/SUGGESTED_INFLUENCERS_SUCCEEDED',
  SUGGESTED_INFLUENCERS_FAILED = 'INFLUENCERS/SUGGESTED_INFLUENCERS_FAILED',
  UPDATE_INSTAGRAM_STATUS = 'INFLUENCERS/UPDATE_INSTAGRAM_STATUS',
}

export enum INFLUENCERS_LIST_ACTIONS {
  REQUEST_SAVED_LISTS = 'INFLUENCERS/REQUEST_SAVED_LISTS',
  RECEIVE_SAVED_LISTS = 'INFLUENCERS/RECEIVE_SAVED_LISTS',
  REQUEST_SAVED_LISTS_BY_USER = 'INFLUENCERS/REQUEST_SAVED_LISTS_BY_USER',
  RECEIVE_SAVED_LISTS_BY_USER = 'INFLUENCERS/RECEIVE_SAVED_LISTS_BY_USER',
  REQUEST_ADD_USER_TO_LIST = 'INFLUENCERS/REQUEST_ADD_USER_TO_LIST',
  RECEIVE_ADDED_USER_TO_LIST = 'INFLUENCERS/RECEIVE_ADDED_USER_TO_LIST',
  REQUEST_REMOVE_USER_FROM_LIST = 'INFLUENCERS/REQUEST_REMOVE_USER_FROM_LIST',
  RECEIVE_REMOVED_USER_FROM_LIST = 'INFLUENCERS/RECEIVE_REMOVED_USER_FROM_LIST',
  RECEIVE_ADDED_USER_TO_LIST_ERROR = 'INFLUENCERS/RECEIVE_ADDED_USER_TO_LIST_ERROR',
  REQUEST_CREATE_LIST = 'INFLUENCERS/REQUEST_CREATE_LIST',
  RECEIVE_CREATED_LIST = 'INFLUENCERS/RECEIVE_CREATED_LIST',
  CLEAR_CREATE_LIST = 'INFLUENCERS/CLEAR_CREATE_LIST',
  REQUEST_TRACKED_INFLUENCERS = 'INFLUENCERS/REQUEST_TRACKED_INFLUENCERS',
  RECEIVE_TRACKED_INFLUENCERS = 'INFLUENCERS/RECEIVE_TRACKED_INFLUENCERS',
  ADD_TRACKED_INFLUENCER = 'INFLUENCERS/ADD_TRACKED_INFLUENCER',
  UPDATE_TRACKED_INFLUENCERS = 'INFLUENCERS/UPDATE_TRACKED_INFLUENCERS',
  UPDATE_INFLUENCER_LISTS = 'INFLUENCERS/UPDATE_INFLUENCER_LISTS',
}

export const INFLUENCER_MEDIA_CARD_MEDIA_SIZE = 212;

export const SOURCE_TRACKING_LAG_MS = 90000; // 1.5 * 60 * 1000 = 1.5 minutes

export const INFLUENCERS_DELETE_LIST_WARNING = `Are you sure you would like to delete this list?
Creators will still be tracked and can be viewed in any other lists in which they've been added.`;

export const INFLUENCERS_UNTRACK_WARNING = `Deleting this creator from your
account will remove them from all Creator Lists, Collections, and Gallery streams. The
creator will no longer count towards your creator limit.`;

export const INFLUENCERS_OVER_LIMIT_WARNING = `You've reached your creator limit. Please reach out
to your Bazaarvoice representative to increase your limit, or consider removing some creators
to continue.`;

export const INFLUENCERS_BULK_ADD_STATUS: Record<
  IInfluencerBulkAddStatusCode,
  Omit<IInfluencersBulkAddStatus, 'statusCode'>
> = {
  BusinessUserAdded: {
    message: 'Successfully Added',
    isFailure: false,
  },
  NonBusinessUserAdded: {
    message: 'Successfully Added (Limited Tracking)',
    tooltip: `This user is a non-business account.
     As a result, you are only able to view and track their Instagram posts that mention or tag your brand.`,
    isFailure: false,
  },
  UserNotFound: {
    message: 'Creator not found',
    isFailure: true,
  },
  FailedToAddUser: {
    message: 'Failed',
    isFailure: true,
  },
  UserExists: {
    message: 'This creator is already in this list.',
    isFailure: true,
  },
  UserNotSupported: {
    message: "User's social network is not supported",
    isFailure: true,
  },
} as const;

export const CREATOR_STATS = {
  FOLLOWERS: 'FOLLOWERS',
  POSTS: 'POSTS',
  POSTS_FT_BRAND: 'POSTS FEAT. MY BRAND',
  FOLLOWER_ENG_RATE: 'FOLLOWER ENG. RATE',
  AVG_EMV: 'AVG. EMV',
  AVG_VIEWS: 'AVG. VIEWS',
};

export const INFLUENCERS_USER_CARD_STATS = {
  FOLLOWERS: 'FOLLOWERS',
  POSTS_FT_BRAND: 'POSTS FEAT. MY BRAND',
  FOLLOWER_ENG_RATE: 'FOLLOWER ENG. RATE',
  AVG_EMV: 'AVG. EMV',
};

export const CREATOR_STATS_TOOLTIPS = {
  POSTS_FT_BRAND:
    'The total number of posts that this creator has' +
    ' published mentioning or tagging your brand.',
  FOLLOWER_ENG_RATE:
    'This is the average Follower Engagement Rate across recent posts.' +
    ' Follower Engagement Rate is calculated by taking the likes plus comments of each' +
    ' post and dividing by followers.',
  AVG_EMV:
    'This is the average Earned Media Value across recent posts. Earned Media' +
    " Value is the estimated value of the impressions that this creator's posts have" +
    ' generated.',
};

export const INFLUENCERS_USER_CARD_STAT_TOOLTIPS = {
  POSTS_FT_BRAND:
    'The total number of posts that this creator has' +
    ' published mentioning or tagging your brand.',
  FOLLOWER_ENG_RATE:
    'This is the average Follower Engagement Rate across recent posts.' +
    ' Follower Engagement Rate is calculated by taking the likes plus comments of each' +
    ' post and dividing by followers.',
  AVG_EMV:
    'This is the average Earned Media Value across recent posts. Earned Media' +
    " Value is the estimated value of the impressions that this creator's posts have" +
    ' generated.',
};

export enum INFLUENCERS_LIST_MEDIA_SORT {
  LIKES = 'likes',
  TIME = 'time',
  COMMENTS = 'comments',
  EMV = 'emv',
  ENGAGEMENT = 'engagement',
}

export const INFLUENCER_MEDIA_CARD_ICONS = {
  COMMENTS: 'comments',
  ENGAGEMENT: 'multiple-users',
  EMV: 'emv',
  LIKES: 'likes',
  INSTAGRAM: 'instagram',
  VIDEO: 'instagram-video',
} as const;

export const INFLUENCER_MEDIA_CARD_STAT_TOOLTIPS = {
  ENGAGEMENT:
    'Follower Engagement Rate: The number of likes plus the number of ' +
    'comments divided by the number of current followers.',
  EMV: 'Earned Media Value: The estimated value of the impressions that this posts has generated.',
  LIKES: 'Likes: Number of times this post was liked.',
  COMMENTS: 'Comments: Number of comments this post received.',
  POSTS: 'Number of posts matching the current filters.',
};

export const INFLUENCER_STAT_BUBBLE_TITLES = {
  COMMENTS: 'Total Comments',
  EMV: 'Average EMV',
  ENGAGEMENT: 'Follower Eng. Rate',
  LIKES: 'Total Likes',
  POSTS: 'Total Posts',
};

export const INFLUENCER_STAT_BUBBLE_TOOLTIPS = {
  COMMENTS: 'Number of comments associated with the filtered set of media.',
  EMV: 'Average Earned Media Value per post in the filtered set of Instagram media.',
  ENGAGEMENT:
    'Average Engagement Rate of the posts in the filtered set of media.',
  LIKES: 'Number of likes associated with the filtered set of media.',
  POSTS: 'Number of posts matching the current filters.',
};

export const INFLUENCER_DETAILS_STAT_BUBBLE_TITLES = {
  AVG_COMMENTS: 'Average Comments',
  TOTAL_COMMENTS: 'Total Comments',
  EMV: 'Average EMV',
  ENGAGEMENT: 'Follower Engagement Rate',
  AVG_LIKES: 'Average Likes',
  TOTAL_LIKES: 'Total Likes',
};

export const INFLUENCER_DETAILS_STAT_BUBBLE_TOOLTIPS = {
  AVG_COMMENTS:
    'Average number of comments per post in the filtered set of media.',
  TOTAL_COMMENTS:
    'Number of comments associated with the filtered set of media.',
  EMV: 'Average Earned Media Value per post in the filtered set of media.',
  ENGAGEMENT:
    'Average Follower Engagement Rate of the posts in the filtered set of media.',
  AVG_LIKES: 'Average number of likes per post in the filtered set of media.',
  TOTAL_LIKES:
    'Total number of likes associated with the filtered set of media.',
};

export const INFLUENCER_SEARCH_LIMITS = {
  FOLLOWERS_LOWER: 2000,
  FOLLOWERS_UPPER: 1000000,
  ESTIMATED_ENGAGEMENT_RATE_LOWER: 0,
  ESTIMATED_ENGAGEMENT_RATE_UPPER: 5,
  TOTAL_POSTS_LOWER: 10,
  TOTAL_POSTS_UPPER: 2000,
};

export type InfluencersSortDir = 'asc' | 'desc';

export const INFLUENCER_TABLE_HEADERS = {
  SELECT_CREATOR_CHECKBOX: {
    title: 'SELECT CREATOR TO ADD TO ANOTHER LIST',
    helpText: undefined,
    helpTextTitle: undefined,
    sortType: undefined,
    trackingId: undefined,
  },
  INFLUENCER: {
    title: 'INFLUENCER',
    helpText: undefined,
    helpTextTitle: undefined,
    sortType: undefined,
    trackingId: undefined,
  },
  CREATOR: {
    title: 'CREATOR',
    helpText: undefined,
    helpTextTitle: undefined,
    sortType: undefined,
    trackingId: undefined,
  },
  FOLLOWERS: {
    title: 'FOLLOWERS',
    helpText: 'Total number of followers this creator has.',
    helpTextTitle: undefined,
    sortType: CreatorListSort.FOLLOWERS,
    trackingId:
      InfluencersTrackingIds.CREATOR_LISTS_USERS_TABLE_HEADER_SORT_FOLLOWERS,
  },
  POSTS_FT_BRAND: {
    title: 'POSTS FT. MY BRAND',
    helpText: `Total number of posts from this creator that your brand is mentioned or tagged in. This metric only applies to Instagram and TikTok.`,
    helpTextTitle: 'Posts Featuring My Brand',
    sortType: CreatorListSort.POSTS_FT_BRAND,
    trackingId:
      InfluencersTrackingIds.CREATOR_LISTS_USERS_TABLE_HEADER_SORT_POSTS_FT_BRAND,
  },
  FOLLOWER_ENG_RATE: {
    title: 'FOLLOWER ENG. RATE',
    helpText:
      'The number of likes plus the number of comments divided by the number of current followers this creator has.',
    helpTextTitle: 'Follower Engagement Rate',
    sortType: CreatorListSort.FOLLOWER_ENG_RATE,
    trackingId:
      InfluencersTrackingIds.CREATOR_LISTS_USERS_TABLE_HEADER_SORT_ENGAGEMENT_RATE,
  },
  AVG_EMV: {
    title: 'AVG. EMV',
    helpText: 'The estimated value that this creator is providing your brand.',
    helpTextTitle: 'Average Earned Media Value',
    sortType: CreatorListSort.AVG_EMV,
    trackingId:
      InfluencersTrackingIds.CREATOR_LISTS_USERS_TABLE_HEADER_SORT_AVG_EMV,
  },
} as const;

export const HARMONY_CREATOR_TABLE_HEADERS = {
  CREATOR: 'CREATOR',
  BIO: 'BIO',
  FOLLOWERS: 'FOLLOWERS',
  FOLLOWER_ENG_RATE: 'FOLLOWER ENG. RATE',
  ACTION: 'ACTION',
} as const;

export const HARMONY_SORT_ITEMS_NAME = {
  RELEVANCE: 'Relevance',
  FOLLOWERS: 'Highest Followers',
  ENGAGEMENT: 'Highest Follower Eng. Rate',
};

export const CREATOR_DEFAULT_USER_FETCH_COUNT = 16;

export const CREATOR_TABLE_USER_COUNT_OPTIONS = [8, 16, 32, 64];

export const CREATOR_LIST_EMPTY_TEXT = {
  EMPTY_SEARCH_AND_FILTERS:
    'There are no results that match your search and filters.',
  EMPTY_LIST:
    "Looks like you haven't added any Creators to this list yet. " +
    'Get started by Exploring Creators or manually adding Creators to this list.',
};

export const INFLUENCER_NETWORKS = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
};

export type ProfileNetwork = Extract<
  SocialNetwork,
  'Instagram' | 'TikTok' | 'YouTube'
>;
export const PROFILE_NETWORKS_LIST: ProfileNetwork[] = [
  SocialNetwork.Instagram,
  SocialNetwork.TikTok,
  SocialNetwork.YouTube,
];

export type INFLUENCER_RIGHTS_SETTING = keyof typeof INFLUENCER_RIGHTS_SETTINGS;

export const INFLUENCER_RIGHTS_SETTINGS = {
  DEFAULT: 'Default',
  MENTIONED_AND_TAGGED: 'MentionedAndTagged',
  ALL_MEDIA: 'AllMedia',
};

export const INFLUENCER_RIGHTS_TEXT = {
  LIST: {
    CTA: 'Configure how rights are approved on media published by Influencers within this list.',
    CTA_IG_ONLY:
      'Configure how rights are approved on media published by Instagram Influencers within this list.',
    DEFAULT: {
      LABEL: 'Require Rights Requests on All Media',
      CAPTION:
        'By default, you will have to request rights on all media published by Influencers in this list. ' +
        "Select this option if you'd like to pick and choose which media you'd like to request rights on.",
    },
    MENTIONED_AND_TAGGED: {
      LABEL: 'Auto-approve Rights on Mentioned/Tagged Media',
      CAPTION:
        'You will automatically have rights approved for all media mentioning or tagging your brand that is ' +
        'published by Influencers in this list.',
    },
    ALL_MEDIA: {
      LABEL: 'Auto-approve Rights on All Media in this List',
      CAPTION:
        'You will automatically have rights approved for all media published by Influencers in this list.',
    },
  },
  DETAILS: {
    CTA: 'Configure how rights are approved on media published by this Influencer.',
    DEFAULT: {
      LABEL: 'Require Rights Requests on All Media',
      CAPTION:
        'By default, you will have to request rights on all media published by this Influencer. ' +
        "Select this option if you'd like to pick and choose which media you'd like to request rights on.",
    },
    MENTIONED_AND_TAGGED: {
      LABEL: 'Auto-approve Rights on Mentioned/Tagged Media',
      CAPTION:
        'You will automatically have rights approved for all media mentioning or tagging your brand that is ' +
        'published by this Influencer.',
    },
    ALL_MEDIA: {
      LABEL: 'Auto-approve Rights on All Media from this Influencer',
      CAPTION:
        'You will automatically have rights approved for all media published by this Influencer.',
    },
  },
};

export const INFLUENCER_RIGHTS_STATUSES = {
  SUCCESS: 'StatusSuccess',
};

export enum INFLUENCER_CONTENT_SOURCES {
  API = 'api',
  USS = 'uss',
}

export const HarmonyAiFilterNames = {
  HARMONY_AI_PLATFORM_FILTER: 'Platform',
  HARMONY_AI_GENDER_FILTER: 'Sex',
  HARMONY_AI_AGE_RANGE_FILTER: 'Age Range',
  HARMONY_AI_LOOKALIKES_FILTER: 'Find Similar',
  HARMONY_AI_BRANDS_FILTER: 'Brand Mentions',
  HARMONY_AI_LOCATIONS_FILTER: 'Location',
  HARMONY_AI_LANGUAGES_FILTER: 'Language',
  HARMONY_AI_FOLLOWER_ENGAGEMENT_RATE: 'Follower Eng. Rate',
  HARMONY_AI_FOLLOWER_COUNT_FILTER: 'Follower Count',
  HARMONY_AI_SAFETY_SCORE_FILTER: 'Safety Score',
  HARMONY_AI_BIOGRAPHY_FILTER: 'Biography',
  HARMONY_AI_SOURCE_FILTER: 'Verification Source',
  HARMONY_AI_LAST_POSTED_AT_FILTER: 'Last Posted',
  HARMONY_AI_ADDITIONAL_FILTERS: 'Additional Filters',
} as const;

export enum SafetyTier {
  VERY_SAFE = 'VERY_SAFE',
  SAFE = 'SAFE',
  CAUTION = 'CAUTION',
  SAFETY_RISK = 'SAFETY_RISK',
  UNSAFE = 'UNSAFE',
}

export const SafetyTierTrackingIds = {
  [SafetyTier.VERY_SAFE]:
    InfluencersTrackingIds.HARMONY_AI_SAFETY_SCORE_FILTER_VERY_SAFE,
  [SafetyTier.SAFE]: InfluencersTrackingIds.HARMONY_AI_SAFETY_SCORE_FILTER_SAFE,
  [SafetyTier.CAUTION]:
    InfluencersTrackingIds.HARMONY_AI_SAFETY_SCORE_FILTER_CAUTION,
  [SafetyTier.SAFETY_RISK]:
    InfluencersTrackingIds.HARMONY_AI_SAFETY_SCORE_FILTER_SAFETY_RISK,
  [SafetyTier.UNSAFE]:
    InfluencersTrackingIds.HARMONY_AI_SAFETY_SCORE_FILTER_UNSAFE,
} as const satisfies Record<SafetyTier, TrackingId>;

export const HARMONY_LAST_POSTED_DURATION_OPTIONS = [
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 3 months' },
  { days: 365, label: 'Last 1 year' },
];

export enum ListContentPlatformFilter {
  All = 'all',
  Instagram = 'instagram',
  TikTok = 'tiktok',
  YouTube = 'youtube',
}

export const CreatorDetailsInsightsInviteToRegisterText =
  'Invite this creator to register with Bazaarvoice to view';

export const BV_BADGE_TOOLTIP_TEXT =
  'These creators have authenticated their account with Bazaarvoice';

export const INFLUENSTER_PRO_BADGE_TOOLTIP_TEXT =
  'These creators have authenticated their account with InfluensterPro';

// Some features are only supported by some profile networks.
// This maps network support by feature
export const FEATURE_NETWORK_MAP = {
  ADULT_AUDIENCE_PERCENTAGE: [SocialNetwork.Instagram],
  CITY_DISTRIBUTION: [SocialNetwork.Instagram],
  AGE_GENDER_DISTRIBUTION: [SocialNetwork.Instagram],
  BRAND_AFFINITY: [SocialNetwork.Instagram],
  STORY_AVERAGE_ENGAGEMENT: [SocialNetwork.Instagram],
  AVERAGE_VIEWS: [SocialNetwork.TikTok],
  AVERAGE_SHARES: [SocialNetwork.TikTok],
  POSTS_FT_BRAND: [SocialNetwork.TikTok, SocialNetwork.Instagram],
  SIMILAR_CREATORS: [
    SocialNetwork.TikTok,
    SocialNetwork.Instagram,
    SocialNetwork.YouTube,
  ],
  INSIGHTS: [
    SocialNetwork.TikTok,
    SocialNetwork.Instagram,
    SocialNetwork.YouTube,
  ],
  FOLLOWERS_BY_LOCATION: [SocialNetwork.TikTok, SocialNetwork.Instagram],
};

export const INSTAGRAM_USERNAME_REGEX = /instagram\.com\/(.+?)(\/|$)/;
export const TIKTOK_USERNAME_REGEX = /tiktok\.com\/@(.+?)(\/|$)/;
export const YOUTUBE_USERNAME_REGEX = /youtube\.com\/@(.+?)(\/|$)/;
